import { useMemo } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import Typography from '@mui/material/Typography'
import type { IQuestionBankEntryDetail } from '@brushfire/types'
import {
  InstrumentPreviewRequiredMark,
  QuestionLivePreview,
} from '@/features/instruments/question-builder/QuestionBuilderPanels'
import { formatQuestionBankSavedAgoLabel } from '@/features/question-bank/lib/formatQuestionBankRelativeTime'
import { libraryModalCloseButtonSx } from '@/features/library/lib/libraryModalCloseButtonSx'
import {
  bankEntryToPreviewBuilderQuestion,
  questionBankHasAnswerOptions,
} from '@/features/question-bank/lib/questionBankMappers'
import { QuestionBankTypeBadge } from './QuestionBankTypeBadge'

/** Figma 6074:14140 — Question Bank detail modal */
const PANEL_BG = '#f0f8fd'
const BORDER = '#e4e9f0'
const TITLE = '#0d1b2a'
const META_LABEL = '#3f4850'
const META_VALUE = '#0b1c30'

type Props = Readonly<{
  open: boolean
  entry: IQuestionBankEntryDetail | null
  onClose: () => void
}>

function DetailMetaColumn({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <Box sx={{ minWidth: 0, flex: '1 1 0' }}>
      <Typography
        sx={{
          fontSize: '9px',
          lineHeight: '14px',
          fontWeight: 500,
          letterSpacing: '0.12px',
          color: META_LABEL,
          mb: '-0.5px',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '18px',
          lineHeight: '32px',
          fontWeight: 600,
          color: META_VALUE,
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
    </Box>
  )
}

function formatUsedInLabel(count: number): string {
  return `${count} Instrument${count === 1 ? '' : 's'}`
}

export function QuestionBankDetailDialog({ open, entry, onClose }: Props) {
  const previewQuestion = useMemo(
    () => (entry ? bankEntryToPreviewBuilderQuestion(entry) : null),
    [entry],
  )

  if (!entry || !previewQuestion) return null

  const hasOptions = questionBankHasAnswerOptions(entry)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.5)' } } }}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '16px',
          border: `0.5px solid ${BORDER}`,
          maxWidth: 900,
          width: '100%',
          overflow: 'hidden',
          bgcolor: '#fff',
        },
      }}
      aria-labelledby="question-bank-detail-title"
      data-testid="question-bank-detail-dialog"
    >
      {/* Figma 6074:14141 — header row */}
      <Box
        sx={{
          borderBottom: `1px solid ${BORDER}`,
          minHeight: '75px',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          pl: '40.5px',
          pr: '28px',
          pt: '20px',
          pb: '21px',
        }}
      >
        <QuestionBankTypeBadge apiType={entry.type} compact />
      </Box>

      {/* Figma 6074:14148 — question + answer preview panel */}
      <Box sx={{ bgcolor: PANEL_BG, px: '40.5px', pt: '30.5px', pb: '30px' }}>
        <Typography
          id="question-bank-detail-title"
          component="div"
          sx={{
            fontWeight: 700,
            fontSize: '20px',
            lineHeight: '30px',
            color: TITLE,
            wordBreak: 'break-word',
            mb: '20px',
          }}
        >
          {entry.prompt}
          {previewQuestion.mandatory ? <InstrumentPreviewRequiredMark /> : null}
        </Typography>

        {hasOptions ? (
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '30px',
              color: TITLE,
              mb: '9px',
            }}
          >
            Answer Options
          </Typography>
        ) : null}

        <QuestionLivePreview
          q={previewQuestion}
          showHeading={false}
          surface="survey"
        />
      </Box>

      {/* Figma 6074:14170–14182 — metadata + Close */}
      <Box
        sx={{
          bgcolor: '#fff',
          px: '40.5px',
          pt: '28px',
          pb: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 2, sm: '48px' },
            maxWidth: '100%',
          }}
          data-testid="question-bank-detail-metadata"
        >
          <DetailMetaColumn label="Saved by" value={entry.savedByName} />
          <DetailMetaColumn label="Used in" value={formatUsedInLabel(entry.usageCount)} />
          <DetailMetaColumn label="Saved" value={formatQuestionBankSavedAgoLabel(entry.createdAt)} />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={onClose}
            data-testid="question-bank-detail-close"
            sx={libraryModalCloseButtonSx}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Dialog>
  )
}
